import { getAccountProductsServices } from 'services/product-service/getAccountProductsServices';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AccountType, CustomError } from 'types';
import { searchAccount } from 'services/accounts/searchAccount';
import { closeModal, openModal } from './modalReducer';
import { AccountSearchResult } from 'pco-product-service-internal-consumer';

const uniqueDefined = (values: (string | undefined)[]): string[] =>
  Array.from(new Set(values)).filter((value): value is string => value !== undefined);

export const initialAccountState: AccountType = {
  accountNumberFromParam: undefined,
  accountSearchState: undefined,
  brUid: undefined,
  isLoading: false,
  accountTable: {
    statusFilter: [],
    typeFilter: [],
    selectedProduct: undefined,
    tableItems: [],
  },
  newAccountsTable: {
    statusFilter: [],
    typeFilter: [],
  },
  error: undefined,
  accountDetails: undefined,
};

export const searchAccountThunk = createAsyncThunk('ACCOUNT/searchAccount', async (accountNumber: string, thunkAPI) => {
  try {
    return await searchAccount(accountNumber);
  } catch (error: unknown) {
    thunkAPI.dispatch(
      openModal({
        modalType: 'JBErrorModalWrapper',
        modalProps: {
          httpError: error,
          okHandler: () => {
            thunkAPI.dispatch(closeModal());
          },
          technicalInfo: {
            title: 'Error when searching account',
            userErrorMessage: `An error occurred when searching account. Please try again later.`,
          },
        },
      }),
    );
  }
});

export const fetchAccountTableThunk = createAsyncThunk(
  'ACCOUNT/fetchTable',
  async (accountNumber: string, thunkAPI) => {
    try {
      const result = await getAccountProductsServices(accountNumber);
      return result.productsServicesOutput;
    } catch (error: unknown) {
      thunkAPI.dispatch(
        openModal({
          modalType: 'JBErrorModalWrapper',
          modalProps: {
            httpError: error,
            okHandler: () => {
              thunkAPI.dispatch(closeModal());
            },
            technicalInfo: {
              title: 'Error when retrieving account product and services',
              userErrorMessage: `An error occurred when retrieving account product and services. Please try again later.`,
            },
          },
        }),
      );
    }
  },
);

const accountSlice = createSlice({
  name: 'ACCOUNT',
  initialState: initialAccountState,
  reducers: {
    setAccountSearch: (state, action: PayloadAction<string | undefined>) => {
      return { ...state, accountSearchState: action.payload };
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoading: action.payload };
    },
    setAccountNumber: (state, action: PayloadAction<string | undefined>) => {
      return { ...state, accountNumberFromParam: action.payload };
    },
    setAccountSelectedProduct: (state, action: PayloadAction<string | undefined>) => {
      return {
        ...state,
        accountTable: {
          ...state.accountTable,
          selectedProduct: action.payload,
        },
      };
    },
    setAccountError: (state, action: PayloadAction<CustomError | undefined>) => {
      return { ...state, error: action.payload };
    },
    setAccountDetails: (state, action: PayloadAction<AccountSearchResult | undefined>) => {
      return { ...state, accountDetails: action.payload };
    },
    setBrUid: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        brUid: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(searchAccountThunk.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(searchAccountThunk.fulfilled, (state, action) => {
      return { ...state, isLoading: false, accountDetails: action.payload };
    });
    builder.addCase(searchAccountThunk.rejected, (state, action) => {
      return {
        ...state,
        isLoading: false,
        error: action.payload as CustomError,
      };
    });
    builder.addCase(fetchAccountTableThunk.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchAccountTableThunk.fulfilled, (state, action) => {
      const items = action.payload ?? [];
      const existingItems = items.filter((item) => !item.isRequiredNewBr);
      const newBrItems = items.filter((item) => item.isRequiredNewBr);

      return {
        ...state,
        isLoading: false,
        accountTable: {
          ...state.accountTable,
          statusFilter: uniqueDefined(existingItems.map((item) => item.status)),
          typeFilter: uniqueDefined(existingItems.map((item) => item.type)),
          tableItems: items,
        },
        newAccountsTable: {
          statusFilter: uniqueDefined(newBrItems.map((item) => item.status)),
          typeFilter: uniqueDefined(newBrItems.map((item) => item.type)),
        },
      };
    });
    builder.addCase(fetchAccountTableThunk.rejected, (state, action) => {
      return {
        ...state,
        isLoading: false,
        error: action.payload as CustomError,
      };
    });
  },
});

export const {
  setAccountSearch,
  setIsLoading,
  setAccountNumber,
  setAccountSelectedProduct,
  setAccountError,
  setAccountDetails,
  setBrUid,
} = accountSlice.actions;
export default accountSlice.reducer;
